import React, { useMemo, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import type { CheckoutIntentResponse } from '../../services/payments';
import { bookingBrandFontFamilyVar } from '../../styles/tebox-booking';

type PaymentBrandColors = {
  textColor?: string;
  buttonColor?: string;
  primaryTextColor?: string;
  borderRadius?: string;
};

type InnerProps = {
  intent: CheckoutIntentResponse;
  amountCents: number;
  onSuccess: (result: { paymentIntentId?: string; setupIntentId?: string }) => void;
  onError: (message: string) => void;
  disabled?: boolean;
  brandColors?: PaymentBrandColors;
};

function PaymentFormInner({
  intent,
  amountCents,
  onSuccess,
  onError,
  disabled,
  brandColors,
}: InnerProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements || disabled) return;

    setSubmitting(true);
    try {
      if (intent.intentType === 'payment' && intent.clientSecret) {
        const result = await stripe.confirmPayment({
          elements,
          redirect: 'if_required',
        });
        if (result.error) {
          onError(result.error.message || 'Payment failed');
          return;
        }
        const piId = result.paymentIntent?.id ?? intent.intentId;
        onSuccess({ paymentIntentId: piId });
        return;
      }

      if (intent.intentType === 'setup' && intent.clientSecret) {
        const result = await stripe.confirmSetup({
          elements,
          redirect: 'if_required',
        });
        if (result.error) {
          onError(result.error.message || 'Could not save card');
          return;
        }
        const siId = result.setupIntent?.id ?? intent.intentId;
        onSuccess({ setupIntentId: siId });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const amountLabel =
    amountCents > 0 ? `$${(amountCents / 100).toFixed(2)} due now` : 'Save card on file';

  return (
    <form onSubmit={(e) => void onSubmit(e)} className="space-y-4">
      <p
        style={{
          fontFamily: bookingBrandFontFamilyVar,
          fontWeight: 500,
          color: brandColors?.textColor,
          margin: 0,
        }}
      >
        {amountLabel}
      </p>
      <PaymentElement />
      <button
        type="submit"
        disabled={!stripe || submitting || disabled}
        className="w-full py-3 px-6 font-medium disabled:opacity-50"
        style={{
          fontFamily: bookingBrandFontFamilyVar,
          borderRadius: brandColors?.borderRadius ?? '6px',
          backgroundColor: brandColors?.buttonColor ?? '#021B1B',
          color: brandColors?.primaryTextColor ?? '#FFFFFF',
          border: 'none',
          cursor: submitting || disabled ? 'not-allowed' : 'pointer',
        }}
      >
        {submitting ? 'Processing…' : amountCents > 0 ? 'Pay and continue' : 'Save card and continue'}
      </button>
    </form>
  );
}

type Props = {
  intent: CheckoutIntentResponse;
  amountCents: number;
  onSuccess: (result: { paymentIntentId?: string; setupIntentId?: string }) => void;
  onError: (message: string) => void;
  disabled?: boolean;
  brandColors?: PaymentBrandColors;
};

export function OnlineBookingPaymentStep({
  intent,
  amountCents,
  onSuccess,
  onError,
  disabled,
  brandColors,
}: Props) {
  const stripePromise = useMemo(() => {
    if (!intent.publishableKey) return null;
    return loadStripe(intent.publishableKey, {
      stripeAccount: intent.stripeAccountId,
    });
  }, [intent.publishableKey, intent.stripeAccountId]);

  if (!intent.clientSecret || !stripePromise) {
    return (
      <p className="text-tebox-caption text-amber-800 bg-amber-50 p-3 rounded-[6px] border border-amber-200">
        Online payments are not available for this club yet.
      </p>
    );
  }

  return (
    <Elements stripe={stripePromise} options={{ clientSecret: intent.clientSecret }}>
      <PaymentFormInner
        intent={intent}
        amountCents={amountCents}
        onSuccess={onSuccess}
        onError={onError}
        disabled={disabled}
        brandColors={brandColors}
      />
    </Elements>
  );
}

export default OnlineBookingPaymentStep;
